import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { endpoints } from "../../lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  LogOut,
  Check,
  Inbox,
  ArrowUpRight,
  LayoutGrid,
  Mic,
  Award,
  BookOpen,
  FileText,
  BadgeCheck,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const RESOURCES = [
  {
    key: "awards",
    label: "Awards",
    icon: Award,
    fields: [
      { name: "year", label: "Year", type: "text", placeholder: "2025" },
      { name: "title", label: "Title", type: "text" },
      { name: "body", label: "Description", type: "textarea" },
      { name: "link", label: "Link", type: "text", placeholder: "https://" },
      { name: "order", label: "Order", type: "number" },
    ],
    defaults: { year: "", title: "", body: "", link: "#", order: 0 },
  },
  {
    key: "speaking",
    label: "Speaking",
    icon: Mic,
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "venue", label: "Venue", type: "text" },
      { name: "topic", label: "Topic", type: "text" },
      { name: "url", label: "URL", type: "text", placeholder: "https://" },
      { name: "order", label: "Order", type: "number" },
    ],
    defaults: { title: "", venue: "", topic: "", url: "", order: 0 },
  },
  {
    key: "insights",
    label: "Insights",
    icon: BookOpen,
    fields: [
      { name: "tag", label: "Tag", type: "text", placeholder: "AI Leadership" },
      { name: "title", label: "Title", type: "text" },
      { name: "slug", label: "Slug", type: "text", placeholder: "auto if blank" },
      { name: "excerpt", label: "Excerpt", type: "textarea" },
      { name: "body_markdown", label: "Body (markdown)", type: "textarea", rows: 10 },
      { name: "date", label: "Date", type: "text", placeholder: "Jul 2025" },
      { name: "read_time", label: "Read time", type: "text", placeholder: "7 min read" },
      { name: "published", label: "Published", type: "boolean" },
      { name: "order", label: "Order", type: "number" },
    ],
    defaults: {
      tag: "General",
      title: "",
      slug: "",
      excerpt: "",
      body_markdown: "",
      date: "",
      read_time: "",
      published: true,
      order: 0,
    },
  },
  {
    key: "certifications",
    label: "Certifications",
    icon: BadgeCheck,
    fields: [
      { name: "name", label: "Name", type: "text" },
      { name: "order", label: "Order", type: "number" },
    ],
    defaults: { name: "", order: 0 },
  },
  {
    key: "case-studies",
    label: "Case studies",
    icon: Briefcase,
    fields: [
      { name: "tag", label: "Tag", type: "text" },
      { name: "title", label: "Title", type: "text" },
      { name: "metric", label: "Headline metric", type: "text", placeholder: "32% faster" },
      { name: "copy", label: "Body", type: "textarea" },
      { name: "order", label: "Order", type: "number" },
    ],
    defaults: { tag: "", title: "", metric: "", copy: "", order: 0 },
  },
  {
    key: "publications",
    label: "Publications",
    icon: FileText,
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "venue", label: "Venue", type: "text" },
      { name: "url", label: "URL", type: "text" },
      { name: "order", label: "Order", type: "number" },
    ],
    defaults: { title: "", venue: "", url: "", order: 0 },
  },
];

function fieldValue(field, form) {
  const v = form[field.name];
  if (v === undefined || v === null) return "";
  return v;
}

function Field({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <label className="block">
        <div className="tag-mono mb-2">{field.label}</div>
        <textarea
          rows={field.rows || 4}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          className="w-full bg-transparent border border-[var(--ink)]/20 focus:border-[var(--molten)] outline-none px-3 py-2 resize-y transition-colors text-sm"
        />
      </label>
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(field.name, e.target.checked)}
          className="w-4 h-4 accent-[var(--molten)]"
        />
        <span className="tag-mono">{field.label}</span>
      </label>
    );
  }
  return (
    <label className="block">
      <div className="tag-mono mb-2">{field.label}</div>
      <input
        type={field.type || "text"}
        value={value}
        onChange={(e) => onChange(field.name, field.type === "number" ? Number(e.target.value || 0) : e.target.value)}
        placeholder={field.placeholder}
        className="w-full bg-transparent border border-[var(--ink)]/20 focus:border-[var(--molten)] outline-none px-3 py-2 transition-colors text-sm"
      />
    </label>
  );
}

function ResourcePanel({ resource }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or 'new'
  const [form, setForm] = useState({ ...resource.defaults });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await endpoints.admin.list(resource.key);
      setItems(data);
    } catch (e) {
      toast.error(`Failed to load ${resource.label}`);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditing("new");
    setForm({ ...resource.defaults });
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({ ...resource.defaults, ...item });
  };

  const cancel = () => {
    setEditing(null);
    setForm({ ...resource.defaults });
  };

  const onFieldChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (editing === "new") {
        await endpoints.admin.create(resource.key, form);
        toast.success(`${resource.label} created`);
      } else {
        await endpoints.admin.update(resource.key, editing.id, form);
        toast.success(`${resource.label} updated`);
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.message || "Save failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${resource.label.toLowerCase().slice(0, -1)}?`)) return;
    try {
      await endpoints.admin.remove(resource.key, item.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const primaryField = resource.fields.find((f) => f.name === "title" || f.name === "name") || resource.fields[0];
  const secondaryField = resource.fields.find((f) =>
    ["year", "venue", "tag", "metric", "date"].includes(f.name)
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl" style={{ letterSpacing: "-0.03em", fontWeight: 700 }}>{resource.label}</h2>
          <div className="tag-mono mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</div>
        </div>
        <button onClick={startNew} className="btn-ink">
          <span>New</span> <Plus size={14} />
        </button>
      </div>

      {editing && (
        <div className="mb-8 p-6 border border-[var(--molten)] bg-[var(--cream-soft)]">
          <div className="flex items-center justify-between mb-5">
            <div className="tag-mono">[ {editing === "new" ? "New" : "Edit"} · {resource.label} ]</div>
            <button onClick={cancel} className="w-8 h-8 inline-flex items-center justify-center border border-[var(--ink)]/20 hover:border-[var(--ink)]">
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resource.fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <Field field={f} value={fieldValue(f, form)} onChange={onFieldChange} />
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={save} className="btn-ink">
              <span>Save</span> <Save size={14} />
            </button>
            <button onClick={cancel} className="btn-outline">
              <span>Cancel</span> <X size={14} />
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 tag-mono">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-12 italic-serif text-2xl text-[var(--ink)]/50">No {resource.label.toLowerCase()} yet. Add the first one.</div>
      ) : (
        <ul className="border-t border-[var(--ink)]/15">
          {items.map((it) => (
            <li key={it.id} className="py-5 border-b border-[var(--ink)]/15 flex items-start gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  {secondaryField && it[secondaryField.name] && (
                    <span className="tag-mono">{String(it[secondaryField.name])}</span>
                  )}
                  {typeof it.published === "boolean" && (
                    <span className={`font-mono text-[0.64rem] uppercase tracking-[0.18em] px-2 py-0.5 border ${it.published ? "border-[var(--molten)] text-[var(--molten)]" : "border-[var(--ink)]/30 text-[var(--ink)]/50"}`}>
                      {it.published ? "Published" : "Draft"}
                    </span>
                  )}
                </div>
                <div className="font-serif text-xl md:text-2xl truncate" style={{ letterSpacing: "-0.02em", fontWeight: 600 }}>
                  {it[primaryField.name]}
                </div>
                {it.body || it.excerpt || it.copy || it.topic ? (
                  <div className="text-[var(--ink)]/60 text-sm mt-1 line-clamp-2">{it.body || it.excerpt || it.copy || it.topic}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {it.url && (
                  <a href={it.url} target="_blank" rel="noreferrer" className="w-9 h-9 inline-flex items-center justify-center border border-[var(--ink)]/20 hover:border-[var(--ink)]">
                    <ArrowUpRight size={14} />
                  </a>
                )}
                <button onClick={() => startEdit(it)} className="w-9 h-9 inline-flex items-center justify-center border border-[var(--ink)]/20 hover:border-[var(--ink)]">
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(it)} className="w-9 h-9 inline-flex items-center justify-center border border-[var(--ink)]/20 hover:border-[var(--molten)] hover:text-[var(--molten)]">
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InboxPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await endpoints.admin.inbox();
      setItems(data);
    } catch {
      toast.error("Failed to load inbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await endpoints.admin.removeInbox(id);
      load();
    } catch (e) { toast.error(e.message || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl" style={{ letterSpacing: "-0.03em", fontWeight: 700 }}>Inbox</h2>
          <div className="tag-mono mt-1">{items.length} message{items.length !== 1 ? "s" : ""}</div>
        </div>
      </div>
      {loading ? (
        <div className="py-12 tag-mono">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-12 italic-serif text-2xl text-[var(--ink)]/50">No messages yet.</div>
      ) : (
        <ul className="border-t border-[var(--ink)]/15">
          {items.map((m) => (
            <li key={m.id} className="py-6 border-b border-[var(--ink)]/15">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span className="tag-mono">{m.topic || "General"}</span>
                    <span className="tag-mono">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <div className="font-serif text-xl md:text-2xl" style={{ letterSpacing: "-0.02em", fontWeight: 600 }}>
                    {m.name} <span className="text-[var(--ink)]/50">·</span>{" "}
                    <a className="link-underline text-[var(--molten)]" href={`mailto:${m.email}`}>{m.email}</a>
                    {m.org ? <span className="text-[var(--ink)]/60 text-base ml-2">· {m.org}</span> : null}
                  </div>
                  <p className="mt-3 text-[var(--ink)]/75 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
                <button onClick={() => remove(m.id)} className="w-9 h-9 inline-flex items-center justify-center border border-[var(--ink)]/20 hover:border-[var(--molten)] hover:text-[var(--molten)]">
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("awards");

  const tabs = useMemo(
    () => [
      ...RESOURCES.map((r) => ({ key: r.key, label: r.label, icon: r.icon })),
      { key: "inbox", label: "Inbox", icon: Inbox },
    ],
    []
  );

  return (
    <main className="min-h-[80vh] bg-[var(--cream)] text-[var(--ink)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-24">
        {/* header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag-mono mb-3">[ Admin · Control room ]</div>
            <h1 className="headline-lg">
              Hello, <span className="italic-serif text-[var(--molten)]">{user?.name?.split(" ")[0] || "Drijesh"}.</span>
            </h1>
            <div className="tag-mono mt-3">{user?.email}</div>
          </div>
          <div className="flex gap-3">
            <a href="/" className="btn-outline">
              <span>View site</span> <ArrowUpRight size={14} />
            </a>
            <button onClick={logout} className="btn-ink">
              <span>Sign out</span> <LogOut size={14} />
            </button>
          </div>
        </div>

        {/* tabs */}
        <div className="border-y border-[var(--ink)]/15 mb-10 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {tabs.map((t) => {
              const active = tab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`relative px-4 md:px-5 py-4 flex items-center gap-2 transition-colors ${active ? "text-[var(--ink)]" : "text-[var(--ink)]/55 hover:text-[var(--ink)]"}`}
                >
                  <Icon size={15} />
                  <span className="font-mono text-[0.72rem] uppercase tracking-[0.18em]">{t.label}</span>
                  {active && <span className="absolute left-4 right-4 bottom-0 h-[2px] bg-[var(--molten)]" />}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "inbox" ? (
          <InboxPanel />
        ) : (
          <ResourcePanel resource={RESOURCES.find((r) => r.key === tab)} />
        )}
      </div>
    </main>
  );
}
